import React from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';
import { IMG } from '../data/realImages';

const SERVICE_IMG = IMG.workforce[1] || IMG.workforce[0];

export default function Workforce() {
  const { t } = useLang();
  const services = t('workforce.services');
  const industries = t('workforce.industries_list');

  const scrollContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="workforce" data-testid="workforce-section" className="relative py-24 md:py-40 border-t border-[#27272A] bg-[#0C0C0E]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header / Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 relative">
            <Reveal className="section-index">{t('workforce.index')}</Reveal>
            <Reveal as="h2" className="h-section text-[#F5F5F2] mt-4 reveal-delay-1">
              {t('workforce.title')}
            </Reveal>
            <Reveal className="mt-6 text-lg text-[#A1A1AA] leading-relaxed reveal-delay-2">
              {t('workforce.sub')}
            </Reveal>
            <Reveal className="mt-10 reveal-delay-3">
              <div className="aspect-[4/5] w-full overflow-hidden border border-[#27272A] relative">
                <img src={SERVICE_IMG} alt="Industrial workforce" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0B]/80" />
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#C8102E] uppercase">B2B</div>
                    <div className="text-white font-display text-2xl mt-1">Industrial Labor Partnerships</div>
                  </div>
                  <ShieldCheck size={28} className="text-white/70" />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal className="text-base md:text-lg text-[#A1A1AA] leading-relaxed">
              {t('workforce.description')}
            </Reveal>

            {/* Services grid */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
              {services.map((s, i) => (
                <Reveal key={s.title} className={`reveal-delay-${(i % 4) + 1}`}>
                  <div data-testid={`workforce-service-${i}`} className="bg-[#101013] p-6 md:p-7 h-full flex flex-col">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-[#C8102E]">0{i + 1}</span>
                      <span className="w-6 h-[1px] bg-[#27272A]" />
                    </div>
                    <h3 className="mt-5 font-display text-xl md:text-2xl tracking-tight text-[#F5F5F2]">{s.title}</h3>
                    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Why */}
            <Reveal className="mt-16 reveal-delay-2">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C8102E]">{t('workforce.why_title')}</div>
              <p className="mt-4 text-base md:text-lg text-[#F5F5F2]/90 leading-relaxed max-w-3xl">
                {t('workforce.why_body')}
              </p>
            </Reveal>

            {/* Industries supported */}
            <Reveal className="mt-12 reveal-delay-3">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]">
                {t('workforce.industries_supported')}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {industries.map((i) => (
                  <span key={i} className="px-4 py-2 border border-[#27272A] text-sm text-[#F5F5F2] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors cursor-default">
                    {i}
                  </span>
                ))}
              </div>
            </Reveal>

            {/* Disclaimer */}
            <Reveal className="mt-12 p-5 border border-[#27272A] bg-[#0A0A0B]/50 reveal-delay-3">
              <p className="text-xs md:text-sm text-[#A1A1AA] leading-relaxed">
                <span className="text-[#C8102E] font-mono tracking-widest">NOTE</span> &nbsp;— &nbsp;{t('workforce.disclaimer')}
              </p>
            </Reveal>

            <Reveal className="mt-10 reveal-delay-4">
              <button onClick={scrollContact} data-testid="workforce-cta-button" className="btn-primary">
                Workforce Inquiry
                <ArrowRight className="arrow" size={16} />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
